import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FinancialAccount, Holding, TaxTreatment } from '../../types/family-finance'
import {
  ACCOUNT_TYPE_LABELS,
  TAX_TREATMENT_LABELS,
  isInvestmentAccount,
} from '../../types/family-finance'
import { listAccounts, listHoldings } from '../../lib/family-finance-api'
import {
  Card,
  Chip,
  EmptyState,
  LoadError,
  LoadingRows,
  StatCard,
  errorMessage,
  money,
  pct,
  relativeTime,
  signedMoney,
} from './shared'
import type { ChipTone } from './shared'

const TREATMENT_TONES: Record<TaxTreatment, ChipTone> = {
  taxable: 'blue',
  traditional: 'amber',
  roth: 'green',
  '529': 'purple',
  hsa: 'green',
  none: 'slate',
}

/**
 * Investments is account-first, holdings-second.
 *
 * The reason is what the sync actually carries: SimpleFIN reports balances and
 * nothing else — no positions, no cost basis. A holdings-only view therefore
 * showed "no holdings synced yet" to an owner whose Schwab brokerage and IRA
 * were sitting right there in Accounts, which read as data loss rather than as
 * a limit of the feed. Accounts are the portfolio; holdings enrich the accounts
 * that happen to report them.
 */
export default function InvestmentsTab() {
  const [holdings, setHoldings] = useState<Holding[]>([])
  const [accounts, setAccounts] = useState<FinancialAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [treatmentFilter, setTreatmentFilter] = useState<TaxTreatment | 'all'>('all')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    Promise.all([listHoldings(), listAccounts()])
      .then(([h, a]) => {
        if (cancelled) return
        setHoldings(h)
        setAccounts(a)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(errorMessage(err))
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  const investmentAccounts = useMemo(
    () =>
      accounts
        .filter(isInvestmentAccount)
        .sort((a, b) => b.balance - a.balance),
    [accounts],
  )

  const holdingsByAccount = useMemo(() => {
    const map = new Map<string, Holding[]>()
    holdings.forEach((h) => {
      map.set(h.accountId, [...(map.get(h.accountId) || []), h])
    })
    return map
  }, [holdings])

  /**
   * Holdings whose account isn't in the investment list — a position reported
   * against an account that was deleted, or one filed under another type.
   * Counting them keeps the portfolio total from quietly dropping value.
   */
  const orphanHoldings = useMemo(() => {
    const known = new Set(investmentAccounts.map((a) => a.id))
    return holdings.filter((h) => !known.has(h.accountId))
  }, [holdings, investmentAccounts])

  const stats = useMemo(() => {
    // The account balance is the authority for what an account is worth; its
    // holdings are a breakdown of that same money, never an addition to it.
    const accountsValue = investmentAccounts.reduce((s, a) => s + a.balance, 0)
    const orphanValue = orphanHoldings.reduce((s, h) => s + h.value, 0)
    const value = accountsValue + orphanValue

    const withBasis = holdings.filter((h) => h.costBasis > 0)
    const basis = withBasis.reduce((s, h) => s + h.costBasis, 0)
    const covered = withBasis.reduce((s, h) => s + h.value, 0)

    const byTreatment = new Map<TaxTreatment, number>()
    const add = (treatment: TaxTreatment, amount: number) =>
      byTreatment.set(treatment, (byTreatment.get(treatment) || 0) + amount)
    investmentAccounts.forEach((a) => add(a.taxTreatment, a.balance))
    orphanHoldings.forEach((h) => add(h.taxTreatment, h.value))

    return {
      value,
      basis,
      covered,
      gain: covered - basis,
      byTreatment,
      taxAdvantaged: value > 0 ? (value - (byTreatment.get('taxable') || 0)) / value : 0,
    }
  }, [investmentAccounts, orphanHoldings, holdings])

  const filteredHoldings = useMemo(
    () =>
      treatmentFilter === 'all'
        ? holdings
        : holdings.filter((h) => h.taxTreatment === treatmentFilter),
    [holdings, treatmentFilter],
  )

  const retry = useCallback(() => setReloadKey((k) => k + 1), [])

  if (loadError) return <LoadError error={loadError} onRetry={retry} />
  if (loading) return <LoadingRows rows={6} />

  if (investmentAccounts.length === 0 && holdings.length === 0) {
    return (
      <Card>
        <EmptyState
          icon="📈"
          message="No investment accounts yet. Brokerage, IRA, 401(k), 529 and HSA accounts appear here as soon as they are linked."
        />
        <p className="text-slate-500 text-xs text-center max-w-lg mx-auto">
          Already linked a brokerage? It may be filed under another type — SimpleFIN sends no
          account-type field, so the service guesses from the account name. Open <span className="text-slate-700 dark:text-slate-300">Accounts</span>,
          click the type on the account, and set it to Brokerage or Retirement.
        </p>
      </Card>
    )
  }

  const treatments = Array.from(stats.byTreatment.entries()).sort((a, b) => b[1] - a[1])
  const accountsWithPositions = investmentAccounts.filter(
    (a) => (holdingsByAccount.get(a.id) || []).length > 0,
  ).length

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Portfolio Value"
          value={money(stats.value)}
          sub={`${investmentAccounts.length} account${investmentAccounts.length === 1 ? '' : 's'}`}
        />
        <StatCard
          label="Unrealized Gain"
          value={stats.basis > 0 ? signedMoney(stats.gain) : '—'}
          sub={
            stats.basis > 0
              ? `${pct(stats.gain / stats.basis)} on ${pct(
                  stats.value > 0 ? stats.covered / stats.value : 0,
                  0,
                )} of the portfolio`
              : 'Needs cost basis — no linked account reports it'
          }
          subTone={stats.basis > 0 ? (stats.gain >= 0 ? 'green' : 'red') : 'slate'}
        />
        <StatCard
          label="Tax-Advantaged"
          value={pct(stats.taxAdvantaged)}
          sub="Roth + Traditional + 529 + HSA share"
        />
      </div>

      {/* Accounts — the part that exists for every linked brokerage. */}
      <Card
        title="Investment Accounts"
        subtitle="Brokerage, retirement, 529 and HSA balances across every linked institution"
      >
        <div className="space-y-2">
          {investmentAccounts.map((account) => {
            const positions = holdingsByAccount.get(account.id) || []
            const positionsValue = positions.reduce((s, h) => s + h.value, 0)
            return (
              <div
                key={account.id}
                className="rounded-lg bg-slate-100/50 dark:bg-slate-900/50 px-3 py-2.5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-slate-900 dark:text-white text-sm font-medium truncate">
                      {account.name}{' '}
                      {account.mask !== '—' && <span className="text-slate-500 font-normal">·· {account.mask}</span>}
                    </p>
                    <p className="text-slate-500 text-xs flex items-center gap-1.5 flex-wrap mt-0.5">
                      <span className="truncate">
                        {account.institutionName} · {account.owner}
                      </span>
                      <Chip tone="slate">{ACCOUNT_TYPE_LABELS[account.type]}</Chip>
                      <Chip tone={TREATMENT_TONES[account.taxTreatment]}>
                        {TAX_TREATMENT_LABELS[account.taxTreatment]}
                      </Chip>
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-slate-900 dark:text-white text-sm font-medium tabular-nums">
                      {money(account.balance, 2)}
                    </p>
                    <p className="text-slate-500 text-xs">{relativeTime(account.lastUpdated)}</p>
                  </div>
                </div>

                {positions.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60 space-y-1">
                    {positions.map((h) => (
                      <div key={h.id} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600 dark:text-slate-300 truncate mr-3">
                          <span className="font-medium text-slate-800 dark:text-slate-200">{h.symbol}</span>{' '}
                          {h.quantity.toLocaleString()} @ {money(h.price, 2)}
                        </span>
                        <span className="text-slate-700 dark:text-slate-200 tabular-nums flex-shrink-0">
                          {money(h.value)}
                        </span>
                      </div>
                    ))}
                    {/* Positions rarely foot exactly to the balance (cash,
                        pending trades, a partial feed) — say so instead of
                        letting the two numbers look like a contradiction. */}
                    {Math.abs(positionsValue - account.balance) > 1 && (
                      <p className="text-slate-500 text-xs pt-0.5">
                        Positions total {money(positionsValue)} — the {money(Math.abs(account.balance - positionsValue))}{' '}
                        difference is cash or positions the feed doesn't itemize.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {accountsWithPositions < investmentAccounts.length && (
          <p className="text-slate-500 text-xs mt-3">
            {accountsWithPositions === 0
              ? 'No positions itemized. SimpleFIN authorizes balances only, so linked brokerages report what an account is worth but not what it holds — the totals above are complete, the security-level detail is not available from this connection.'
              : `${investmentAccounts.length - accountsWithPositions} of ${investmentAccounts.length} accounts report a balance without itemized positions — SimpleFIN authorizes balances only.`}
          </p>
        )}
      </Card>

      {/* Tax treatment allocation */}
      {stats.value > 0 && (
        <Card title="Allocation by Tax Treatment" subtitle="Where the next dollar of gains gets taxed">
          <div className="space-y-2.5">
            {treatments.map(([treatment, value]) => (
              <div key={treatment} className="flex items-center gap-3">
                <div className="w-44 flex-shrink-0">
                  <Chip tone={TREATMENT_TONES[treatment]}>{TAX_TREATMENT_LABELS[treatment]}</Chip>
                </div>
                <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${(value / stats.value) * 100}%` }}
                  />
                </div>
                <p className="w-28 text-right text-sm text-slate-800 dark:text-slate-200 flex-shrink-0">
                  {money(value)} <span className="text-slate-500 text-xs">({pct(value / stats.value, 0)})</span>
                </p>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-xs mt-3">
            Treatment comes from each account's own setting — correct one in Accounts and this moves with it.
          </p>
        </Card>
      )}

      {/* Holdings table — only where positions actually came through. */}
      {holdings.length > 0 && (
        <Card
          title="Holdings"
          subtitle="Security-level detail, for the accounts whose feed itemizes it"
          action={
            <select
              value={treatmentFilter}
              onChange={(e) => setTreatmentFilter(e.target.value as TaxTreatment | 'all')}
              className="bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm rounded-lg px-2 py-1.5 border border-slate-300 dark:border-slate-600 outline-none"
            >
              <option value="all">All treatments</option>
              <option value="taxable">Taxable</option>
              <option value="traditional">Traditional</option>
              <option value="roth">Roth</option>
              <option value="529">529</option>
            </select>
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-2 pr-3 font-medium">Symbol</th>
                  <th className="text-left py-2 pr-3 font-medium hidden md:table-cell">Account</th>
                  <th className="text-right py-2 pr-3 font-medium hidden sm:table-cell">Qty</th>
                  <th className="text-right py-2 pr-3 font-medium hidden sm:table-cell">Price</th>
                  <th className="text-right py-2 pr-3 font-medium">Value</th>
                  <th className="text-right py-2 font-medium">Gain</th>
                </tr>
              </thead>
              <tbody>
                {filteredHoldings.map((h) => {
                  const gain = h.value - h.costBasis
                  return (
                    <tr key={h.id} className="border-b border-slate-200/50 dark:border-slate-700/50 last:border-0">
                      <td className="py-2.5 pr-3">
                        <p className="text-slate-900 dark:text-white font-medium">{h.symbol}</p>
                        <p className="text-slate-500 text-xs truncate max-w-[180px]">{h.name}</p>
                      </td>
                      <td className="py-2.5 pr-3 hidden md:table-cell">
                        <p className="text-slate-600 dark:text-slate-300 text-xs">{h.accountName}</p>
                        <Chip tone={TREATMENT_TONES[h.taxTreatment]}>{h.taxTreatment}</Chip>
                      </td>
                      <td className="py-2.5 pr-3 text-right text-slate-600 dark:text-slate-300 hidden sm:table-cell">
                        {h.quantity.toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-slate-600 dark:text-slate-300 hidden sm:table-cell">{money(h.price, 2)}</td>
                      <td className="py-2.5 pr-3 text-right text-slate-900 dark:text-white font-medium">{money(h.value)}</td>
                      <td
                        className={`py-2.5 text-right font-medium ${
                          h.costBasis > 0
                            ? gain >= 0
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-red-600 dark:text-red-400'
                            : 'text-slate-500'
                        }`}
                      >
                        {h.costBasis > 0 ? signedMoney(gain) : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="text-slate-500 text-xs mt-3">
            Unrealized gains in taxable accounts are what matter for tax-loss harvesting and charitable giving;
            Roth and 529 gains are tax-free when used correctly, Traditional is taxed as income at withdrawal.
          </p>
        </Card>
      )}
    </div>
  )
}
